// Remote X10 configuration (jcore /v1/controltower/x10/{x10Id}/configuration).
// The unit never reports its settings back, so every change carries a sync status that jcore infers from what the
// unit reports afterwards (heartbeats, warnings). Values are in display units: °C for offsets, L for volume.

export type X10ConfigKey = "V_OFFSET" | "A_OFFSET" | "HALO" | "V_FAIL_MODE" | "VOLUME_LEFT";

export type X10ConfigCommandSource =
  | "CONTROL_TOWER"
  | "X10_APP"
  | "BOOTUP_RESYNC"
  | "HEARTBEAT_RESYNC";

export type X10ConfigCommandStatus =
  | "PENDING"
  | "SENT"
  | "DELIVERED"
  | "COMPLETED"
  | "UNCONFIRMED"
  | "NOT_DELIVERED"
  | "SUPERSEDED"
  | "FAILED";

export type X10ConfigItemStatus =
  | "PENDING"
  | "SENT"
  | "UNCONFIRMED"
  | "DELIVERED"
  | "VERIFIED"
  | "MISMATCH"
  | "OVERRIDDEN_BY_DEVICE"
  | "NOT_DELIVERED"
  | "SUPERSEDED"
  | "FAILED";

export type X10ConfigBlockedReason =
  | "FEATURE_DISABLED"
  | "READ_ONLY_ROLE"
  | "NOT_PROVISIONED"
  | "OFFLINE_OR_UNVERIFIED"
  | "BUSY"
  | "FIRMWARE_UNSUPPORTED"
  | "NO_ACTIVE_KEG";

export type X10ConfigValue = number | boolean | null;

export interface IX10ConfigChange {
  commandId: string;
  source: X10ConfigCommandSource;
  value: X10ConfigValue;
  status: X10ConfigItemStatus;
  observed: X10ConfigValue;
  requestedBy: string | null;
  requestedAt: string | null;
  sentAt: string | null;
  verifiedAt: string | null;
}

export interface IX10ConfigCommandItem {
  key: X10ConfigKey;
  value: X10ConfigValue;
  status: X10ConfigItemStatus;
  observed: X10ConfigValue;
  verifiedAt: string | null;
}

export interface IX10ConfigCommand {
  id: string;
  source: X10ConfigCommandSource;
  requestedBy: string | null;
  requestedAt: string | null;
  sentAt: string | null;
  deliveredAt: string | null;
  status: X10ConfigCommandStatus;
  statusDetail: string | null;
  items: IX10ConfigCommandItem[];
}

export interface IX10Configuration {
  x10Id: string;
  serialNumber: string | null;
  firmware: string | null;
  online: {
    /** jcore processed a heartbeat in the last 2 minutes: changes can be sent. */
    verified: boolean;
    lastHeardAt: string | null;
    lastHeardSecondsAgo: number | null;
    lastHeartbeat: string | null;
    lastBootup: string | null;
    lastPing: string | null;
    alive: boolean;
  };
  canApply: boolean;
  blockedReason: X10ConfigBlockedReason | null;
  retryAfterSeconds: number | null;
  supports: { offsets: boolean; failMode: boolean; halo: boolean; volume: boolean; ping: boolean };
  vesselOffset: { desired: number | null; lastChange: IX10ConfigChange | null };
  ambientOffset: { desired: number | null; lastChange: IX10ConfigChange | null };
  halo: { desired: boolean | null; lastChange: IX10ConfigChange | null };
  coolingFailMode: {
    reported: boolean | null;
    reportedAt: string | null;
    lastChange: IX10ConfigChange | null;
  };
  volume: { activeKeg: boolean; kegLiters: number | null; lastChange: IX10ConfigChange | null };
  inFlight: IX10ConfigCommand | null;
}

export interface IX10ConfigurationUpdate {
  vesselOffset?: number;
  ambientOffset?: number;
  halo?: boolean;
  coolingFailMode?: boolean;
  volumeLeftLiters?: number;
}

export interface IX10ConfigPingResult {
  pinged: boolean;
  reason: string | null;
  retryAfterSeconds: number | null;
}

/** Body of a 409 from POST /configuration. */
export interface IX10ConfigBlocked {
  reason: X10ConfigBlockedReason;
  message: string;
  retryAfterSeconds: number | null;
}

export interface IX10ConfigState {
  configurations: { [x10Id: string]: IX10Configuration };
  history: { [x10Id: string]: IX10ConfigCommand[] };
  loading: boolean;
  loadError: boolean;
  applying: boolean;
  /** Set when the last apply was refused (409); cleared by the next apply or a successful one. */
  applyBlocked: IX10ConfigBlocked | null;
  lastPing: IX10ConfigPingResult | null;
}

export const START_X10_CONFIG_POLLING = "x10config/START_X10_CONFIG_POLLING";
export const STOP_X10_CONFIG_POLLING = "x10config/STOP_X10_CONFIG_POLLING";
export const GET_X10_CONFIG = "x10config/GET_X10_CONFIG";
export const GET_X10_CONFIG_SUCCESS = "x10config/GET_X10_CONFIG_SUCCESS";
export const GET_X10_CONFIG_ERROR = "x10config/GET_X10_CONFIG_ERROR";
export const GET_X10_CONFIG_HISTORY = "x10config/GET_X10_CONFIG_HISTORY";
export const GET_X10_CONFIG_HISTORY_SUCCESS = "x10config/GET_X10_CONFIG_HISTORY_SUCCESS";
export const PING_X10 = "x10config/PING_X10";
export const PING_X10_DONE = "x10config/PING_X10_DONE";
export const APPLY_X10_CONFIG = "x10config/APPLY_X10_CONFIG";
export const APPLY_X10_CONFIG_SUCCESS = "x10config/APPLY_X10_CONFIG_SUCCESS";
export const APPLY_X10_CONFIG_ERROR = "x10config/APPLY_X10_CONFIG_ERROR";

export interface IStartX10ConfigPollingAction {
  type: typeof START_X10_CONFIG_POLLING;
  payload: string;
}

export interface IStopX10ConfigPollingAction {
  type: typeof STOP_X10_CONFIG_POLLING;
}

export interface IGetX10ConfigAction {
  type: typeof GET_X10_CONFIG;
  payload: string;
}

export interface IGetX10ConfigSuccessAction {
  type: typeof GET_X10_CONFIG_SUCCESS;
  payload: IX10Configuration;
}

export interface IGetX10ConfigErrorAction {
  type: typeof GET_X10_CONFIG_ERROR;
}

export interface IGetX10ConfigHistoryAction {
  type: typeof GET_X10_CONFIG_HISTORY;
  payload: string;
}

export interface IGetX10ConfigHistorySuccessAction {
  type: typeof GET_X10_CONFIG_HISTORY_SUCCESS;
  payload: { x10Id: string; history: IX10ConfigCommand[] };
}

export interface IPingX10Action {
  type: typeof PING_X10;
  payload: string;
}

export interface IPingX10DoneAction {
  type: typeof PING_X10_DONE;
  payload: IX10ConfigPingResult | null;
}

export interface IApplyX10ConfigAction {
  type: typeof APPLY_X10_CONFIG;
  payload: { x10Id: string; update: IX10ConfigurationUpdate };
}

export interface IApplyX10ConfigSuccessAction {
  type: typeof APPLY_X10_CONFIG_SUCCESS;
  payload: { x10Id: string; command: IX10ConfigCommand };
}

export interface IApplyX10ConfigErrorAction {
  type: typeof APPLY_X10_CONFIG_ERROR;
  payload: IX10ConfigBlocked | null;
}

export type X10ConfigActionTypes =
  | IStartX10ConfigPollingAction
  | IStopX10ConfigPollingAction
  | IGetX10ConfigAction
  | IGetX10ConfigSuccessAction
  | IGetX10ConfigErrorAction
  | IGetX10ConfigHistoryAction
  | IGetX10ConfigHistorySuccessAction
  | IPingX10Action
  | IPingX10DoneAction
  | IApplyX10ConfigAction
  | IApplyX10ConfigSuccessAction
  | IApplyX10ConfigErrorAction;
